import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import axios from 'axios';

import { useDebounceState } from 'features/common/hooks/useDebounceState';
import { IProviderDTO } from '../../types';
import { autocomplete } from '../../services';

const MIN_SEARCH_LENGTH = 4;

interface ProvidersAdvancedSearchProps {
	selectedProvider: IProviderDTO | null;
	onSelect: (provider: IProviderDTO) => void;
	onClear: () => void;
}

export const formatProviderAutocompleteLabel = (option: IProviderDTO): string => {
	const parts = [option.fantasyName, option.businessName, option.rut, option.email].filter(Boolean);
	return parts.join(' · ');
};

export const ProvidersAdvancedSearch = ({
	selectedProvider,
	onSelect,
	onClear,
}: ProvidersAdvancedSearchProps): JSX.Element => {
	const [searchTerm, setSearchTerm] = useState('');
	const [options, setOptions] = useState<IProviderDTO[]>([]);
	const [loading, setLoading] = useState(false);
	const debouncedSearchTerm = useDebounceState(searchTerm, 300);

	useEffect(() => {
		if (selectedProvider) {
			setLoading(false);
			return;
		}

		const term = debouncedSearchTerm.trim();
		if (term.length < MIN_SEARCH_LENGTH) {
			setOptions([]);
			setLoading(false);
			return;
		}

		const abortController = new AbortController();

		const searchProviders = async () => {
			setLoading(true);
			try {
				const { data } = await autocomplete(term);
				if (abortController.signal.aborted) return;
				setOptions(data);
			} catch (error) {
				if (axios.isCancel(error)) return;
				setOptions([]);
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false);
				}
			}
		};

		searchProviders();

		return () => {
			abortController.abort();
		};
	}, [debouncedSearchTerm, selectedProvider]);

	return (
		<Box sx={{ mt: 2 }}>
			<Autocomplete
				fullWidth
				size="small"
				value={selectedProvider}
				options={options}
				loading={loading}
				filterOptions={(items) => items}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				getOptionLabel={formatProviderAutocompleteLabel}
				noOptionsText={
					debouncedSearchTerm.trim().length < MIN_SEARCH_LENGTH
						? `Ingresá al menos ${MIN_SEARCH_LENGTH} caracteres`
						: 'Sin resultados'
				}
				onChange={(_, value, reason) => {
					if (value) {
						onSelect(value);
					} else {
						onClear();
						if (reason === 'clear') {
							setSearchTerm('');
						}
					}
				}}
				onInputChange={(_, value, reason) => {
					if (reason !== 'input') return;
					if (selectedProvider) onClear();
					setSearchTerm(value);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Búsqueda avanzada"
						placeholder="Nombre fantasía, razón social, RUT o email"
						helperText="Encontrá un proveedor y filtrá la tabla por su ID"
						inputProps={{ ...params.inputProps, autoComplete: 'off' }}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? <CircularProgress color="inherit" size={18} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>
		</Box>
	);
};
